import { c as _c } from "react/compiler-runtime";
import { useIntl } from '@onefootprint/hooks';
import { IcoArrowTopRight16, IcoEye16 } from '@onefootprint/icons';
import type { AmlHit } from '@onefootprint/request-types/dashboard';
import { CopyButton, LinkButton, Tag } from '@onefootprint/ui';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import TruncatedText from '@/entities/components/details/components/truncated-text';
import toReadableString from '@/utils/to-readable-string';
import { EMPTY_VALUE } from '../../../../constants';
type AmlHitItemProps = {
  hit: AmlHit;
};
const isUrl = (value: unknown): value is string => typeof value === 'string' && /^https?:\/\//.test(value);

// Screening and match types keep the provider's spelling (`sanction`, `pep-class-2`), which is how
// rules and the node's `is_*_hit` flags refer to them. Field labels can't: the client camelCases
// every untagged response key, so `Legal Basis` arrives as `legalBasis`.
const AmlHitItem = t0 => {
  const $ = _c(36);
  const {
    hit: t1
  } = t0;
  const {
    name,
    screeningTypes,
    matchTypes,
    fields,
    media
  } = t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "onboardings.watchlist.amlList"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t2);
  const {
    formatDateWithShortMonth
  } = useIntl();
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[1] !== fields || $[2] !== matchTypes || $[3] !== name || $[4] !== screeningTypes || $[5] !== t) {
    const fieldEntries = fields && typeof fields === "object" && !Array.isArray(fields) ? Object.entries(fields).filter(_temp).sort(_temp2) : [];
    t8 = "w-full p-3 border border-tertiary rounded-md flex gap-3 overflow-hidden";
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <div><IcoEye16 /></div>;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    t3 = "flex flex-col gap-2 flex-1 min-w-0";
    const t10 = name ?? EMPTY_VALUE;
    if ($[14] !== t10) {
      t4 = <h3 className="text-label-2 text-primary">{t10}</h3>;
      $[14] = t10;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    if ($[16] !== screeningTypes) {
      t5 = screeningTypes && screeningTypes.length > 0 && <div className="flex flex-wrap gap-1">{screeningTypes.map(_temp3)}</div>;
      $[16] = screeningTypes;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    if ($[18] !== matchTypes || $[19] !== t) {
      t6 = matchTypes && matchTypes.length > 0 && <p className="text-body-2 text-secondary">{t(_temp4, {
          list: matchTypes.join(", ")
        })}</p>;
      $[18] = matchTypes;
      $[19] = t;
      $[20] = t6;
    } else {
      t6 = $[20];
    }
    t7 = fieldEntries.length > 0 && <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">{fieldEntries.map(t11 => {
        const [key, value_0] = t11;
        return <Fragment key={key}><dt className="text-body-2 text-tertiary">{toReadableString(key)}</dt><dd className="text-body-2 text-primary min-w-0">{isUrl(value_0) ? <div className="flex items-center gap-1 min-w-0"><p className="truncate">{value_0}</p><CopyButton ariaLabel={t(_temp5)} contentToCopy={value_0} tooltip={{
                position: "bottom"
              }} /></div> : <p className="truncate">{String(value_0)}</p>}</dd></Fragment>;
      })}</dl>;
    $[1] = fields;
    $[2] = matchTypes;
    $[3] = name;
    $[4] = screeningTypes;
    $[5] = t;
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
    $[12] = t9;
  } else {
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
    t6 = $[9];
    t7 = $[10];
    t8 = $[11];
    t9 = $[12];
  }
  let t10;
  if ($[21] !== formatDateWithShortMonth || $[22] !== media || $[23] !== t) {
    t10 = media && media.length > 0 && <div className="flex flex-col gap-1"><p className="text-label-2 text-tertiary">{t(_temp6)}</p>{media.map(t11 => {
        const {
          date,
          title,
          snippet,
          url
        } = t11;
        const label = [title ?? EMPTY_VALUE, date && formatDateWithShortMonth(new Date(date))].filter(Boolean).join(" \u22C5 ");
        return <div key={url ?? label} className="flex flex-col gap-1">{url ? <LinkButton href={url} iconComponent={IcoArrowTopRight16} target="_blank">{label}</LinkButton> : <p className="text-body-2 text-secondary truncate">{label}</p>}{snippet && <TruncatedText text={snippet} truncatedTextNumLines={3} className="text-body-2" />}</div>;
      })}</div>;
    $[21] = formatDateWithShortMonth;
    $[22] = media;
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t3 || $[27] !== t4 || $[28] !== t5 || $[29] !== t6 || $[30] !== t7) {
    t11 = <div className={t3}>{t4}{t5}{t6}{t7}{t10}</div>;
    $[25] = t10;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== t11 || $[33] !== t8 || $[34] !== t9) {
    t12 = <div className={t8}>{t9}{t11}</div>;
    $[32] = t11;
    $[33] = t8;
    $[34] = t9;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  return t12;
};
export default AmlHitItem;
function _temp(t0) {
  const [, value] = t0;
  return Boolean(value);
}
function _temp2(t0, t1) {
  const [a] = t0;
  const [b] = t1;
  return a.localeCompare(b);
}
function _temp3(type) {
  return <Tag key={type}>{type}</Tag>;
}
function _temp4($) {
  return $.matchTypes;
}
function _temp5($_0) {
  return $_0.copyUrl;
}
function _temp6($_1) {
  return $_1.relevantMedia;
}